import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { useThemePalette } from "common/hooks/theme_palette";
import {
  addEventTypeServiceRequest,
  getAvailableEventServicesRequest,
  getEventTypeDetailsRequest,
  getEventTypeServicesRequest,
  removeEventTypeServiceRequest,
} from "services/modules/events/eventTypes";

const EventTypeServicesPanel = ({
  eventTypeId,
  canUpdate,
  services: servicesProp,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { oliveGreen } = useThemePalette();
  const servicesFromStore = useSelector((state) => state.eventTypes.services);
  const products = useSelector(
    (state) => state.eventTypes.availableServices,
  );
  const productsLoading = useSelector((state) => state.eventTypes.load);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [displayPriority, setDisplayPriority] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editPriority, setEditPriority] = useState(0);
  const searchTimeoutRef = useRef(null);
  const isServicesLoading =
    productsLoading?.loading &&
    productsLoading?.type === "available-services";

  const services =
    Array.isArray(servicesProp) && servicesProp.length > 0
      ? servicesProp
      : servicesFromStore || [];

  const refreshServices = () => {
    if (!eventTypeId) return;
    dispatch(getEventTypeDetailsRequest({ id: eventTypeId }));
    dispatch(getEventTypeServicesRequest({ id: eventTypeId }));
  };

  const fetchProducts = (filterObj = {}) => {
    if (!eventTypeId) return;
    dispatch(
      getAvailableEventServicesRequest({
        params: {
          page_size: 20,
          page_number: 1,
          keyword: "",
          event_type_id: eventTypeId,
          available: true,
          ...filterObj,
        },
      }),
    );
  };

  useEffect(() => {
    if (!servicesProp?.length) {
      refreshServices();
    }
    fetchProducts();
  }, [eventTypeId]);

  const handleSearchInputChange = (_event, value, reason) => {
    setSearchInput(value);
    if (reason !== "input") return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchProducts({ keyword: value || "" });
    }, 400);
  };

  const handleAdd = () => {
    const productId = selectedProduct?.id;
    if (!productId) return;
    dispatch(
      addEventTypeServiceRequest({
        id: eventTypeId,
        body: {
          product_id: Number(productId),
          display_priority: Number(displayPriority) || 0,
        },
        action: () => {
          toast.success(t("events_event_types.service_added"));
          setSelectedProduct(null);
          setSearchInput("");
          setDisplayPriority(0);
          refreshServices();
          fetchProducts();
        },
      }),
    );
  };

  const startEdit = (service) => {
    setEditingId(service?.product_id);
    setEditPriority(service?.display_priority ?? 0);
  };

  const handleUpdate = (product_id) => {
    dispatch(
      addEventTypeServiceRequest({
        id: eventTypeId,
        body: {
          product_id: Number(product_id),
          display_priority: Number(editPriority) || 0,
        },
        action: () => {
          toast.success(t("events_event_types.service_updated"));
          setEditingId(null);
          refreshServices();
        },
      }),
    );
  };

  const handleRemove = (product_id) => {
    dispatch(
      removeEventTypeServiceRequest({
        id: eventTypeId,
        product_id,
        action: () => {
          toast.success(t("events_event_types.service_removed"));
          refreshServices();
        },
      }),
    );
  };

  const linkedIds = new Set(services.map((s) => s?.product_id));
  const productOptions = (products || [])
    .filter((p) => !linkedIds.has(p.id))
    .map((p) => ({
      ...p,
      name:
        p?.localized_data?.en?.name ||
        p?.localized_data?.name_en ||
        p?.name ||
        `#${p.id}`,
    }));

  return (
    <Paper
      elevation={0}
      sx={{ p: 2.5, border: "1px solid #E8E8E8", borderRadius: "10px" }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1}
        mb={2.5}
      >
        <Box>
          <Typography fontSize="18px" fontWeight="600">
            {t("events_event_types.services_title")}
          </Typography>
          <Typography fontSize="13px" color="#797979">
            {services.length} {t("events_event_types.services_count")}
          </Typography>
        </Box>
      </Stack>

      {canUpdate && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: "8px",
            backgroundColor: "#FAFAFA",
            border: "1px dashed #DCDCDC",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Autocomplete
              size="small"
              options={productOptions}
              value={selectedProduct}
              inputValue={searchInput}
              onInputChange={handleSearchInputChange}
              onChange={(_event, value) => setSelectedProduct(value)}
              onOpen={() => {
                if (!productOptions?.length) fetchProducts();
              }}
              loading={isServicesLoading}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(options) => options}
              noOptionsText={t("events_event_types.services_empty")}
              sx={{ minWidth: 260, flex: 1, backgroundColor: "#fff" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("events_event_types.select_product")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isServicesLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              size="small"
              type="number"
              label={t("events_event_types.display_priority")}
              value={displayPriority}
              onChange={(e) => setDisplayPriority(e.target.value)}
              sx={{ width: { xs: "100%", sm: 140 }, backgroundColor: "#fff" }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={!selectedProduct}
              sx={{
                backgroundColor: oliveGreen,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: oliveGreen, opacity: 0.9 },
              }}
            >
              {t("events_event_types.add_service")}
            </Button>
          </Stack>
        </Paper>
      )}

      {services.length === 0 ? (
        <Box
          sx={{
            py: 5,
            textAlign: "center",
            borderRadius: "8px",
            backgroundColor: "#FAFAFA",
            border: "1px solid #F0F0F0",
          }}
        >
          <Typography color="#8A8A8A">
            {t("events_event_types.services_empty")}
          </Typography>
        </Box>
      ) : (
        <Stack gap={1.5}>
          {services.map((service) => {
            const pid = service?.product_id;
            const options = service?.options || [];
            const isEditing = editingId === pid;

            return (
              <Paper
                key={pid}
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid #E8E8E8",
                  borderRadius: "10px",
                  transition: "border-color 0.15s ease",
                  "&:hover": { borderColor: "#C8C8C8" },
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  gap={2}
                  alignItems={{ xs: "flex-start", md: "flex-start" }}
                >
                  <Box
                    sx={{
                      width: 88,
                      height: 72,
                      borderRadius: "8px",
                      overflow: "hidden",
                      backgroundColor: "#F5F5F5",
                      border: "1px solid #EEEEEE",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {service?.image ? (
                      <img
                        src={service.image}
                        alt={service?.name || ""}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography fontSize="11px" color="#A0A0A0">
                        {t("chat.no_image")}
                      </Typography>
                    )}
                  </Box>

                  <Box flex={1} width="100%">
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={1}
                      mb={1}
                    >
                      <Box>
                        <Typography fontSize="16px" fontWeight="600">
                          {service?.name || "-"}
                        </Typography>
                        <Stack
                          direction="row"
                          gap={1}
                          flexWrap="wrap"
                          mt={0.75}
                          alignItems="center"
                        >
                          <Chip
                            size="small"
                            label={`#${pid}`}
                            sx={{ height: 22, fontSize: "12px" }}
                          />
                          <Chip
                            size="small"
                            label={
                              service?.category?.name ||
                              t("events_event_types.no_category")
                            }
                            sx={{
                              height: 22,
                              fontSize: "12px",
                              backgroundColor: service?.category?.name
                                ? "rgba(33, 150, 243, 0.1)"
                                : "#F0F0F0",
                              color: service?.category?.name
                                ? "#1565C0"
                                : "#757575",
                            }}
                          />
                          {!isEditing && (
                            <Typography fontSize="12px" color="#8A8A8A">
                              {t("events_event_types.display_priority")}:{" "}
                              {service?.display_priority ?? 0}
                            </Typography>
                          )}
                        </Stack>
                      </Box>

                      {canUpdate && !isEditing && (
                        <Stack direction="row" gap={0.5} flexShrink={0}>
                          <Button
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => startEdit(service)}
                            sx={{ textTransform: "none" }}
                          >
                            {t("common.edit")}
                          </Button>
                          <Button
                            color="error"
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => handleRemove(pid)}
                            sx={{ textTransform: "none" }}
                          >
                            {t("common.delete")}
                          </Button>
                        </Stack>
                      )}
                    </Stack>

                    {isEditing && (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        gap={1.5}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        mb={1.5}
                      >
                        <TextField
                          size="small"
                          type="number"
                          label={t("events_event_types.display_priority")}
                          value={editPriority}
                          onChange={(e) => setEditPriority(e.target.value)}
                          sx={{ width: { xs: "100%", sm: 160 } }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleUpdate(pid)}
                          sx={{
                            backgroundColor: oliveGreen,
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: oliveGreen,
                              opacity: 0.9,
                            },
                          }}
                        >
                          {t("common.save")}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setEditingId(null)}
                          sx={{ textTransform: "none" }}
                        >
                          {t("common.clear")}
                        </Button>
                      </Stack>
                    )}

                    <Typography
                      fontSize="12px"
                      color="#8A8A8A"
                      mb={0.75}
                      fontWeight={500}
                    >
                      {t("events_event_types.options")}
                    </Typography>

                    {options.length === 0 ? (
                      <Typography fontSize="13px" color="#A0A0A0">
                        -
                      </Typography>
                    ) : (
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        {options.map((opt) => {
                          const label =
                            opt.option ||
                            (opt.is_master
                              ? t("events_event_types.master_option")
                              : `#${opt.variant_id}`);
                          const price =
                            opt.price_after_discount || opt.price || "-";
                          return (
                            <Chip
                              key={opt.variant_id}
                              size="small"
                              variant="outlined"
                              label={`${label} · ${price} ${t("products.curr_sar")}`}
                              sx={{
                                borderColor: "#E0E0E0",
                                backgroundColor: "#FFF",
                                fontSize: "12px",
                                height: 28,
                              }}
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

export default EventTypeServicesPanel;
